// ============================================================================
// SESSION STATE — Single synchronous "is the user signed in?" predicate
// ============================================================================
// Every auth gate (ProtectedRoute, the TabPresenceGate/AuthLifecycle wiring in
// App.tsx) MUST answer "is the user signed in?" the same way. Two divergent
// predicates is how you get a redirect loop.
//
// This is deliberately SYNCHRONOUS — it reads only what the login flow wrote —
// so route gates can decide on the very first render with no loading flash.
//
// Ported from react-project-template's src/auth/sessionState.ts.

import { getAuthMode } from './modeHelpers';
import { STORAGE_KEYS } from './storageKeys';
import { tokenManager } from './tokenManager';

/**
 * TRUE when this browser holds a usable session.
 *
 * - `tenant-hub` mode: a non-expired access token AND the stored user record
 *   that `AuthContext.authenticate()` writes. The token alone isn't enough —
 *   `AuthContext`'s session recovery needs the user record to rehydrate, so a
 *   token without it would render a shell with no identity.
 * - `legacy` mode: the sample login's own token + user record.
 */
export const hasActiveSession = (): boolean => {
  try {
    const hasStoredUser = !!localStorage.getItem(STORAGE_KEYS.USER);

    if (getAuthMode() === 'tenant-hub') {
      return tokenManager.isAuthenticated() && hasStoredUser;
    }

    return !!localStorage.getItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN) && hasStoredUser;
  } catch {
    // Storage unavailable (private mode quota, disabled cookies) — fail closed.
    return false;
  }
};

export default hasActiveSession;
